import React, { useCallback, useEffect, useState } from 'react';
import { Form, FormField } from 'components/ui/form';
import { useForm } from 'react-hook-form';
import {
  ComboboxGs,
  ComboboxMultiGs,
  InputGs,
  SelectContentProps,
  SelectGroupContentProps,
  SelectGroupGs,
  SelectGs,
  TextareaGs,
} from 'components/ui/Forms';
import {
  useCarrier,
  useWarehouse,
  useOrderType,
  // useOperationType,
  useVehicleType,
  useCargoType,
  useCompany,
  useVehicleSetup,
} from 'hooks/api/general';
import { useOrder } from 'hooks/api/scheduling/useOrder';
import { useDock } from 'hooks/api/scheduling/useDock';
import { useMutation, useQuery } from '@tanstack/react-query';
import { UnloadForm } from './UnloadForm';
import DatePickerGs from 'components/ui/Forms/datePickerGs';
import { Formatter } from 'utils/Formatter';
import { addDays, startOfDay, format, subDays } from 'date-fns';
import { LoadForm } from './LoadForm';
import { Button } from 'components/ui/button';
import { useNavigate } from 'react-router-dom';
import { customMask } from 'utils/customMask';
import { toast } from 'sonner';
import { Product } from 'contracts/Products';
import { SelectOption } from 'contracts/Common';
import { Company } from 'contracts/Companies';
import { VehicleSetup } from 'contracts/VehicleSetups';
import { useAuth } from 'hooks/auth';
import { useDriver } from 'hooks/api/general/useDriver';
import { Switch } from 'components/ui/switch';
import { Label } from 'components/ui/label';
import { AutomaticPayloadOrder } from 'contracts/Orders';

const defaultValues = {
  driverName: '',
  cellPhone: '',
  driverDocument: '',
  companyId: '',
  warehouseId: '1',
  carrierId: '',
  orderTypeId: '2',
  storeType: '',
  operationTypeId: '',
  vehicleTypeId: '',
  cargoTypeId: '',
  vehiclePlate: '',
  scheduledAt: '',
  dockAndTimeframe: '',
  observation: '',
  driverId: '',
};

/** How many days to show when picking a date */
const SCHEDULING_DATE_PICKER_INTERVAL = 365;

// today date in yyyy-mm-dd format
const MIN_DATE = new Date();
const MIN_DATE_CARRIER = addDays(startOfDay(new Date()), 1);
// 15 days from today
const MAX_DATE = addDays(new Date(), SCHEDULING_DATE_PICKER_INTERVAL);
const MAX_DATE_CARRIER = addDays(
  addDays(new Date(), 1),
  SCHEDULING_DATE_PICKER_INTERVAL
)
  .toISOString()
  .split('T')[0];

function formatDate(dateString: string) {
  const date = new Date(dateString);
  return format(date, 'yyyy-MM-dd');
}

function sanitizeOptions(
  options: { name: string; value: number | string | null }[] | undefined
): SelectContentProps[] {
  return (
    options
      ?.filter((o) => o.value)
      .map((option) => ({
        name: option.name,
        value: String(option.value),
      })) || []
  );
}

export const CreateOrder: React.FC = () => {
  const [productList, setProductList] = useState<Product[]>([]);
  const [autoComplete, setAutoComplete] = useState<boolean>(false);
  // const [operationTypeOptions, setOperationTypeOptions] = useState<SelectContentProps[]>([]);
  const [vehicleTypeOptions, setVehicleTypeOptions] = useState<
    SelectContentProps[]
  >([]);
  const [companyOptions, setCompanyOptions] = useState<SelectContentProps[]>(
    []
  );
  const [carrierOptions, setCarrierOptions] = useState<SelectContentProps[]>(
    []
  );
  const [cargoTypeOptions, setCargoTypeOptions] = useState<
    SelectContentProps[]
  >([]);
  const [docksTimeFramesOptions, setDocksTimeFramesOptions] = useState<
    SelectGroupContentProps[]
  >([]);
  const [duration, setDuration] = useState('');
  const [minDate, setMinDate] = useState(MIN_DATE);
  const navigate = useNavigate();

  const { userBelongsToAnyOf, profile } = useAuth();

  const addProductToList = (product: Product[]) => {
    setProductList((prevList) => {
      // Se o produto já existe na lista, deve somar as quantidades
      // const existingProductIndex = prevList.findIndex(p => p.id === product.id);

      // if (existingProductIndex !== -1) {
      //   const newList = [...prevList];
      //   newList[existingProductIndex] = {
      //       ...newList[existingProductIndex],
      //       quantity: newList[existingProductIndex].quantity + product.quantity
      //   };
      //   return newList;
      // } else {
      //   return [...prevList, product];
      // }
      return [...prevList, ...product];
    });
  };

  const updateProductList = (product: Product) => {
    setProductList((prevList) => {
      const existingProductIndex = prevList.findIndex(
        (p) => p.id === product.id
      );

      if (existingProductIndex !== -1) {
        const newList = [...prevList];
        newList[existingProductIndex] = product;
        return newList;
      } else {
        return [...prevList, product];
      }
    });
  };

  const removeProductFromList = (productId: string) => {
    setProductList((prevList) =>
      prevList.filter((product) => product.id !== productId)
    );
  };

  const form = useForm({
    defaultValues,
  });

  const { getDrivers } = useDriver();
  const { getCarriers } = useCarrier();
  const { getWarehouses, getWarehouse } = useWarehouse();
  const { getOrderTypes } = useOrderType();
  // const { getOperationTypes } = useOperationType();
  const { getVehicleTypes } = useVehicleType();
  const { getCargoTypes } = useCargoType();
  const { getCompanies } = useCompany();
  const { getVehicleSetups } = useVehicleSetup();
  const { getDocksTimeFrames } = useDock();
  const { createOrder, getLastOrderByDriver } = useOrder();

  const {
    isPending: isPendingDrivers,
    error: errorDriver,
    data: drivers,
  } = useQuery({
    queryKey: ['getDrivers'],
    queryFn: () => getDrivers(),
    staleTime: 3e5,
    refetchOnWindowFocus: false,
    enabled: autoComplete,
  });

  const lastOrderMutation = useMutation({
    mutationKey: ['getLastOrder', form.watch('driverId')],
    mutationFn: (driverId: string | number) => getLastOrderByDriver(driverId),
    onSuccess: (data) => onLoadLastOrderData(data),
  });

  const onSelectDriver = useCallback(() => {
    if (form.watch('driverId')) {
      const { driverId, ...rest } = defaultValues;
      Object.entries(rest).forEach(([k, v]) => form.setValue(k as any, v)); //evitar campos errôneos em cache
      lastOrderMutation.mutate(form.watch('driverId'));
    }
  }, [form.watch('driverId')]);

  const {
    isPending: isPendingCarrier,
    error: errorCarrier,
    data: carriers,
  } = useQuery({
    queryKey: ['getCarriers'],
    queryFn: () => getCarriers(),
    staleTime: 3e5,
    refetchOnWindowFocus: false,
  });

  const {
    isPending: isPendingWarehouse,
    error: errorWarehouse,
    data: warehouses,
  } = useQuery({
    queryKey: ['getWarehouses'],
    queryFn: () => getWarehouses(),
    staleTime: 3e5,
    refetchOnWindowFocus: false,
  });

  const {
    isPending: isPendingWarehouseId,
    error: errorWarehouseId,
    data: warehouse,
  } = useQuery({
    queryKey: ['getWarehouse', form.watch('warehouseId')],
    queryFn: () => getWarehouse(form.watch('warehouseId')),
    enabled: !!form.watch('warehouseId'),
  });

  const {
    isPending: isPendingVehicleSetup,
    error: errorVehicleSetup,
    data: vehicleSetups,
  } = useQuery({
    queryKey: ['getVehicleSetupsTeste', form.watch('warehouseId')],
    queryFn: () => getVehicleSetups(form.watch('warehouseId')),
    enabled: !!form.watch('warehouseId'),
  });

  const {
    isPending: isPendingOrderType,
    error: errorOrderType,
    data: orderTypes,
  } = useQuery({
    queryKey: ['getOrderTypes'],
    queryFn: () => getOrderTypes(),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  // const { isPending: isPendingOperationType, error: errorOperationType, data: operationTypes } = useQuery({
  //   queryKey: ['getOperationTypes'],
  //   queryFn: () => getOperationTypes(),
  // })

  const {
    isPending: isPendingVehicleType,
    error: errorVehicleType,
    data: vehicleTypes,
  } = useQuery({
    queryKey: ['getVehicleTypes'],
    queryFn: () => getVehicleTypes(),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const {
    isPending: isPendingCargoType,
    error: errorCargoType,
    data: cargoTypes,
  } = useQuery({
    queryKey: ['getCargoTypes'],
    queryFn: () => getCargoTypes(),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const {
    isPending: isPendingCompany,
    error: errorCompany,
    data: companies,
  } = useQuery({
    queryKey: ['getCompanies'],
    queryFn: () => getCompanies(),
    staleTime: 3e5,
    refetchOnWindowFocus: false,
  });

  const formattedDate = form.watch('scheduledAt')
    ? formatDate(form.watch('scheduledAt'))
    : '';

  const {
    isPending: isPendingDocksTimeFrames,
    error: errorDocksTimeFrames,
    data: docksTimeFrames,
  } = useQuery({
    queryKey: [
      'getDocksTimeFrames',
      form.watch('carrierId'),
      form.watch('companyId'),
      form.watch('warehouseId'),
      form.watch('scheduledAt'),
      form.watch('orderTypeId'),
      duration,
    ],
    queryFn: () =>
      getDocksTimeFrames({
        carrierId: form.watch('carrierId'),
        companyId: form.watch('companyId'),
        operationTypeId: '4',
        warehouseId: form.watch('warehouseId'),
        date: formattedDate,
        orderTypeId: form.watch('orderTypeId'),
        duration: duration,
      }),
    enabled:
      !!form.watch('carrierId') &&
      !!form.watch('companyId') &&
      !!form.watch('warehouseId') &&
      !!form.watch('scheduledAt') &&
      !!form.watch('orderTypeId') &&
      !!form.watch('vehicleTypeId') &&
      !!form.watch('cargoTypeId') &&
      !!duration,
  });

  // useEffect(() => {
  //   if(warehouse?.operationTypeOptions) {
  //     setOperationTypeOptions(warehouse.operationTypeOptions.map((operationType: SelectOption) => ({
  //       value: operationType.value,
  //       name: operationType.label,
  //     })))
  //   } else {
  //     setOperationTypeOptions(operationTypes || [])
  //   }

  //   setProductList([]);
  // }, [warehouse, operationTypes])

  useEffect(() => {
    if (warehouse?.vehicleTypesOptions) {
      setVehicleTypeOptions(
        warehouse.vehicleTypesOptions.map((vehicle: SelectOption) => ({
          value: vehicle.value,
          name: vehicle.label,
        }))
      );
    } else {
      setVehicleTypeOptions(vehicleTypes || []);
    }

    setProductList([]);
  }, [warehouse, vehicleTypes]);

  useEffect(() => {
    if (warehouse?.companiesOptions) {
      setCompanyOptions(
        sanitizeOptions(
          warehouse.companiesOptions.map((company: SelectOption) => ({
            value: company.value,
            name: company.label,
          }))
        )
      );
    } else {
      if (companies) {
        const options = companies.map((company: Company) => {
          let displayName = company.tradeName;

          if (company.addressComplement) {
            displayName += ` - ${company.addressComplement}`;
          }

          if (company.ie) {
            displayName += ` - IE: ${company.ie}`;
          }

          return {
            value: company.id,
            name: displayName,
          };
        });

        setCompanyOptions(sanitizeOptions(options));
      }
    }

    setProductList([]);
  }, [warehouse, companies]);

  const vehicleType = form.watch('vehicleTypeId');
  const cargoType = form.watch('cargoTypeId');
  const orderType = form.watch('orderTypeId');
  const companyId = form.watch('companyId');
  // const warehouseId = form.watch("warehouseId")

  useEffect(() => {
    const newProductsList = productList.filter((product) => {
      return companyId.includes(product.companyId);
    });

    setProductList(newProductsList);
  }, [companyId]);

  useEffect(() => {
    if (
      orderType === '1' &&
      (userBelongsToAnyOf('warehouseMember') ||
        userBelongsToAnyOf('admin') ||
        profile?.allowToday === 1)
    ) {
      // Agendamento de Carregamento: Possibilidade do produtor agendar o carregamento no mesmo dia de solicitação.
      setMinDate(subDays(MIN_DATE, 1));
    } else if (
      orderType === '2' &&
      (userBelongsToAnyOf('warehouseMember') || userBelongsToAnyOf('admin'))
    ) {
      // Usuário do Armazém: Somente os usuários do armazém e administradores poderem agendar descarregamento no mesmo dia (encaixe pontual).
      setMinDate(subDays(MIN_DATE, 1));
    } else {
      setMinDate(MIN_DATE);
    }

    //set scheduledAt to empty when changing orderType
    form.setValue('scheduledAt', '');
  }, [orderType]);

  useEffect(() => {
    if (vehicleSetups && vehicleType && cargoType && orderType) {
      const vehicleSetup = vehicleSetups.find((setup: VehicleSetup) => {
        return (
          setup.vehicleType.id === Number(vehicleType) &&
          setup.cargoType.id === Number(cargoType)
        );
      });

      const duration =
        orderType === '1'
          ? vehicleSetup?.loadDuration
          : vehicleSetup?.unloadDuration;

      setDuration(duration?.toString() || '0');
    }
  }, [vehicleSetups, vehicleType, cargoType, orderType]);

  useEffect(() => {
    if (warehouse?.carriersOptions) {
      setCarrierOptions(
        warehouse.carriersOptions.map((carrier: SelectOption) => ({
          value: carrier.value,
          name: carrier.label,
        }))
      );
    } else {
      setCarrierOptions(carriers || []);
    }
  }, [warehouse, carriers]);

  useEffect(() => {
    if (docksTimeFrames) {
      const docksTimeFramesOptions = docksTimeFrames.map(
        ({ dock, timeframes }: any) => ({
          label: dock.name,
          options: timeframes.map((timeframe: any) => ({
            name: `${dock.name} às ${Formatter.date(timeframe, {
              format: 'HH:mm',
            })}`,
            value: `${dock.id}|${timeframe}`,
          })),
        })
      );

      setDocksTimeFramesOptions(docksTimeFramesOptions);
    }
  }, [docksTimeFrames]);

  const onVehicleTypeChange = useCallback(
    (value: string) => {
      const cargoTypeOptions = cargoTypes?.filter((option: any) => {
        return vehicleSetups?.some((setup: any) => {
          return (
            setup.cargoType.id == option.value &&
            setup.vehicleType.id == Number(value)
          );
        });
      });

      setCargoTypeOptions(cargoTypeOptions || []);
      form.setValue('vehicleTypeId', value);
    },
    [form, vehicleSetups, cargoTypes]
  );

  const { mutate: orderMutation, isPending: isCreating } = useMutation({
    mutationFn: (order: any) => createOrder(order),
    onSuccess: () => {
      toast('Solicitação de estoque criada com sucesso');
      navigate('/agendamento/solicitacoes-pendentes');
    },
    onError: (error: any) => {
      toast('Erro ao criar solicitação de estoque', {
        description: error.response.data.message,
      });
    },
  });

  const storeTypes = [
    { value: '1', name: 'AG' },
    { value: '2', name: 'CD' },
  ];

  const handleSubmit = (data: any) => {
    const dockTimeframeParts = data.dockAndTimeframe.split('|');
    const dockId = dockTimeframeParts[0];
    const timeframe = dockTimeframeParts[1];
    let storeType = storeTypes.find(
      (storeType) => storeType.name === 'AG'
    )?.value;

    if (
      data.orderTypeId === '2' &&
      companies?.find(
        (company: any) => Number(company.id) === Number(data.companyId)
      )?.document === '4476442000402'
    ) {
      storeType = storeTypes.find(
        (storeType) => storeType.name === 'CD'
      )?.value;
    }

    //verificar se existem produtos sem volume no descarregamento
    if (
      data.orderTypeId === '2' &&
      productList.some((product) => !product.volume)
    ) {
      toast('Erro ao criar solicitação de estoque', {
        description:
          'Existem produtos sem volume, por favor preencha todos os campos obrigatórios',
      });
      return;
    }

    const submissionData = {
      ...data,
      dockId,
      scheduledAt: timeframe,
      items: productList,
      operationTypeId: '4',
      weightCapacity: 1000, // Verificar de onde buscar info
      duration,
      material: 'material', // Verificar necessidade
      observation: data.observation || null,
      storeType: storeType,
    };

    orderMutation(submissionData);
  };

  const onLoadLastOrderData = async (data: AutomaticPayloadOrder | null) => {
    if (!data) return;

    onVehicleTypeChange(data.vehicleType?.value);
    await new Promise((resolve) => setTimeout(resolve, 10)); // wait setState of cargoTypes to update

    form.reset(
      {
        ...form.getValues(),
        carrierId: data?.carrier?.value || '',
        vehicleTypeId: data?.vehicleType?.value || '',
        vehiclePlate: data?.vehiclePlate || '',
        orderTypeId: data?.orderType?.value || '',
        warehouseId: data?.warehouse?.value || '',
        cargoTypeId: data?.cargoType?.value || '',
        cellPhone: customMask(data.cellPhone, '99 99999-9999'),
        driverDocument: customMask(data.driverDocument, '999.999.999-99'),
        driverName: data?.driverName,
        companyId: (data.relatedCompanies?.map((company) =>
          String(company.value)
        ) || []) as any,
        operationTypeId: data?.operationType?.value || '',
        scheduledAt:
          new Date(data.scheduledAt) > minDate &&
          new Date(data.scheduledAt) < MAX_DATE
            ? data.scheduledAt
            : '',
      },
      {
        keepDirty: true,
        keepTouched: true,
      }
    );
  };

  useEffect(() => {
    onSelectDriver();
  }, [onSelectDriver]);

  return (
    <section className="w-full items-center justify-center">
      <div className="w-full mx-auto bg-white rounded-md p-6 shadow-sm shadow-slate-300">
        <div className="flex items-center justify-start pb-4 gap-3">
          <Switch
            id="autoimport"
            checked={autoComplete}
            onCheckedChange={() => setAutoComplete((prev) => !prev)}
          />
          <Label htmlFor="autoimport">Preenchimento automático</Label>
        </div>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="flex flex-col gap-3"
            id="create-order-form"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
              {autoComplete ? (
                <>
                  <FormField
                    control={form.control}
                    name="driverId"
                    render={({ field }) => (
                      <ComboboxGs
                        {...field}
                        label="Motorista"
                        fieldName="driverId"
                        onValueChange={field.onChange}
                        form={form}
                        content={drivers?.driverOptions || []}
                        fieldValue={field.value}
                        loading={
                          isPendingVehicleType ||
                          isPendingCargoType ||
                          isPendingCompany ||
                          isPendingDrivers ||
                          isPendingCarrier ||
                          isPendingWarehouse ||
                          isPendingOrderType ||
                          lastOrderMutation.isPending ||
                          isPendingVehicleSetup
                        }
                      />
                    )}
                  />
                </>
              ) : (
                <>
                  <FormField
                    control={form.control}
                    name="driverName"
                    render={({ field }) => (
                      <InputGs
                        {...field}
                        label="Nome do motorista"
                        disabled={false}
                        required
                        value={field.value}
                      />
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="cellPhone"
                    render={({ field }) => (
                      <InputGs
                        {...field}
                        label="Celular do motorista"
                        disabled={false}
                        required
                        onChange={(e) => {
                          field.onChange(
                            customMask(e.target.value, '99 99999-9999')
                          );
                        }}
                      />
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="driverDocument"
                    render={({ field }) => (
                      <InputGs
                        {...field}
                        label="CPF do motorista"
                        disabled={false}
                        required
                        minLength={14}
                        maxLength={14}
                        onChange={(e) => {
                          field.onChange(
                            customMask(e.target.value, '999.999.999-99')
                          );
                        }}
                      />
                    )}
                  />
                </>
              )}
              <FormField
                control={form.control}
                name="warehouseId"
                render={({ field }) => (
                  <SelectGs
                    {...field}
                    label="Armazém"
                    content={sanitizeOptions(warehouses) || []}
                    value={field.value}
                    onValueChange={(value) =>
                      form.setValue('warehouseId', value)
                    }
                    required
                    isLoading={isPendingWarehouse}
                  />
                )}
              />
              <FormField
                control={form.control}
                name="companyId"
                render={({ field }) => (
                  // <ComboboxGs
                  //   {...field}
                  //   label="Cliente"
                  //   fieldName="companyId"
                  //   form={form}
                  //   content={companyOptions ?? []}
                  //   fieldValue={field.value}
                  //   loading={isPendingCompany || isPendingWarehouseId || form.watch("warehouseId") === ""}
                  // />
                  <ComboboxMultiGs
                    label="Clientes"
                    options={sanitizeOptions(companyOptions ?? [])}
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Selecione"
                    inputPlaceholder="Buscar..."
                    emptyPlaceholder="Nenhum resultado"
                    multiple
                  />
                )}
              />
              <FormField
                control={form.control}
                name="carrierId"
                render={({ field }) => (
                  <ComboboxGs
                    {...field}
                    label="Transportadora"
                    fieldName="carrierId"
                    form={form}
                    content={sanitizeOptions(carrierOptions ?? [])}
                    fieldValue={field.value}
                    loading={
                      isPendingCarrier ||
                      isPendingWarehouseId ||
                      form.watch('warehouseId') === ''
                    }
                  />
                )}
              />
              <FormField
                control={form.control}
                name="orderTypeId"
                render={({ field }) => (
                  <SelectGs
                    {...field}
                    label="Tipo de solicitação"
                    content={sanitizeOptions(
                      orderTypes?.filter((orderType: SelectContentProps) => {
                        if (userBelongsToAnyOf('carrierMember')) {
                          return Number(orderType.value) === 2;
                        } else {
                          return true;
                        }
                      }) || []
                    )}
                    value={field.value}
                    onValueChange={(value) =>
                      form.setValue('orderTypeId', value)
                    }
                    required
                    isLoading={isPendingOrderType}
                  />
                )}
              />
              {/* <FormField
                control={form.control}
                name="operationTypeId"
                render={({ field }) => (
                  <SelectGs
                    {...field}
                    label="Tipo de operação"
                    content={sanitizeOptions(operationTypeOptions || [])}
                    value={field.value}
                    onValueChange={(value) => form.setValue("operationTypeId", value)}
                    required
                    isLoading={isPendingOperationType || isPendingWarehouseId || form.watch("warehouseId") === ""}
                  />
                )}
              /> */}
              <FormField
                control={form.control}
                name="vehicleTypeId"
                render={({ field }) => (
                  <SelectGs
                    {...field}
                    label="Tipo de veículo"
                    content={sanitizeOptions(vehicleTypeOptions || [])}
                    value={field.value}
                    onValueChange={onVehicleTypeChange}
                    required
                    isLoading={
                      isPendingVehicleType ||
                      isPendingWarehouseId ||
                      form.watch('warehouseId') === ''
                    }
                  />
                )}
              />
              <FormField
                control={form.control}
                name="cargoTypeId"
                render={({ field }) => (
                  <SelectGs
                    {...field}
                    label="Tipo de carga"
                    content={sanitizeOptions(cargoTypeOptions || [])}
                    value={field.value}
                    onValueChange={(value) =>
                      form.setValue('cargoTypeId', value)
                    }
                    required
                    isLoading={
                      isPendingCargoType ||
                      isPendingWarehouseId ||
                      form.watch('vehicleTypeId') === ''
                    }
                  />
                )}
              />
              <FormField
                control={form.control}
                name="vehiclePlate"
                render={({ field }) => (
                  <InputGs
                    {...field}
                    label="Placa do veículo"
                    disabled={false}
                    required
                    value={field.value}
                  />
                )}
              />
              <FormField
                control={form.control}
                name="scheduledAt"
                render={({ field }) => (
                  <DatePickerGs
                    {...field}
                    label="Data"
                    fieldValue={field.value ? new Date(field.value) : undefined}
                    fieldOnChange={(value) =>
                      form.setValue('scheduledAt', value)
                    }
                    minDate={minDate}
                    maxDate={MAX_DATE}
                  />
                )}
              />
              <FormField
                control={form.control}
                name="dockAndTimeframe"
                render={({ field }) => (
                  <SelectGroupGs
                    {...field}
                    label="Doca e horário"
                    content={docksTimeFramesOptions}
                    value={field.value}
                    onValueChange={(value) =>
                      form.setValue('dockAndTimeframe', value)
                    }
                    required
                    isLoading={
                      form.watch('scheduledAt') === undefined ||
                      isPendingDocksTimeFrames
                    }
                  />
                )}
              />
            </div>
            {form.watch('orderTypeId') === '1' && (
              <LoadForm
                addProduct={addProductToList}
                productList={productList}
                removeProduct={removeProductFromList}
                companyIds={form.watch('companyId')}
                warehouseId={form.watch('warehouseId')}
              />
            )}
            {form.watch('orderTypeId') === '2' && (
              <UnloadForm
                addProduct={addProductToList}
                productList={productList}
                updateProduct={updateProductList}
                removeProduct={removeProductFromList}
                companies={companyOptions.filter((company: any) =>
                  form.watch('companyId').includes(String(company.value))
                )}
                // companyIe={companies?.find((company: any) => Number(company.id) === Number(form.watch("companyId")))}
                companyIe={companies?.filter((company: any) =>
                  form.watch('companyId').includes(String(company.id))
                )}
              />
            )}
            <TextareaGs
              name="observation"
              label="Observações"
              value={form.watch('observation')}
              onChange={(e) => form.setValue('observation', e.target.value)}
            />
            <div className="flex justify-end space-x-4 mt-4">
              <Button
                type="button"
                onClick={() => {
                  navigate(-1);
                }}
                variant="secondary"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                form="create-order-form"
                variant="default"
                disabled={productList.length === 0 || isCreating}
              >
                Salvar
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </section>
  );
};
